import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Eye, EyeOff, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LOGO = "/wedifylab_logo3.png";
const HERO_IMG = "https://images.unsplash.com/photo-1780541721627-233827fdbdec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cmsLogin, setCmsLogin] = useState({});

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/cms`)
      .then((r) => setCmsLogin(r.data?.content?.login || {})).catch(() => {});
  }, []);

  const heroImg = cmsLogin.background_upload || cmsLogin.background_url || HERO_IMG;
  const tagline1 = cmsLogin.tagline_1 || "Selamat";
  const tagline2 = cmsLogin.tagline_2 || "datang kembali";
  const heroSubtitle = cmsLogin.subtitle || "Rencanakan hari bahagiamu — budget, vendor, tamu, rundown, hingga undangan digital dalam satu platform.";

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) { toast.error("Lengkapi Username/Email dan Password"); return; }
    setLoading(true);
    const res = await login(identifier, password);
    setLoading(false);
    if (!res.ok) toast.error(res.error || "Login gagal");
    else toast.success("Selamat datang!");
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-[#0a0705] text-white lg:grid-cols-2" data-testid="login-page" style={{ fontFamily: "'Inter', sans-serif" }}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=Inter:wght@400;500;600;700&display=swap" />

      {/* Cinematic hero (desktop only) */}
      <div className="relative hidden overflow-hidden lg:block">
        <motion.img
          src={heroImg} alt="Wedding"
          initial={{ scale: 1.15 }} animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="login-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0705]/95 via-[#0a0705]/50 to-[#0a0705]/70" />
        <div className="grain-overlay absolute inset-0 opacity-[0.15]" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 50% at 30% 30%, #c9a35c22 0%, transparent 70%)" }} />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Wedifylab" className="h-11 w-11 object-contain drop-shadow-[0_0_20px_#c9a35c66]" />
            <div className="leading-tight">
              <p className="text-lg font-semibold text-[#e6c98a]">Wedifylab</p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">Wedding Planner &amp; Invitation</p>
            </div>
          </div>
          <div>
            <div className="overflow-hidden">
              <motion.h1 initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="font-heading text-6xl font-light leading-none tracking-tighter text-white xl:text-7xl" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="login-tagline1">
                {tagline1}
              </motion.h1>
            </div>
            <div className="mt-2 overflow-hidden">
              <motion.h1 initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.58, ease: [0.22, 1, 0.36, 1] }}
                className="text-5xl italic leading-none xl:text-6xl" style={{ fontFamily: "'Great Vibes', cursive", color: "#e6c98a" }} data-testid="login-tagline2">
                {tagline2}
              </motion.h1>
            </div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
              className="mt-6 max-w-md text-sm leading-relaxed text-neutral-300" data-testid="login-hero-subtitle"
              style={{ textShadow: "0 1px 8px rgba(0,0,0,0.75), 0 0 24px rgba(0,0,0,0.55)" }}>
              {heroSubtitle}
            </motion.p>
          </div>
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Sparkles className="h-3.5 w-3.5 text-[#c9a35c]" /> Wedifylab · Black &amp; Gold Edition
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center px-6 py-10 sm:px-12">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          {/* Back to home + Logo (mobile-first) */}
          <div className="mb-8 flex items-center justify-between">
            <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a35c33] px-3 py-1.5 text-xs text-neutral-300 transition-colors hover:border-[#c9a35c] hover:text-[#e6c98a]">
              <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke Beranda
            </Link>
            <Link to="/" className="flex items-center gap-2 lg:hidden" data-testid="login-brand">
              <img src={LOGO} alt="Wedifylab" className="h-8 w-8 object-contain" />
              <span className="text-sm font-semibold text-[#e6c98a]">Wedifylab</span>
            </Link>
          </div>

          {/* Logo top center */}
          <div className="mb-6 flex flex-col items-center text-center">
            <img src={LOGO} alt="Wedifylab" className="h-16 w-16 object-contain drop-shadow-[0_0_18px_#c9a35c66]" data-testid="login-logo" />
            <p className="mt-3 text-[10px] uppercase tracking-[0.35em] text-[#c9a35c]">Wedifylab</p>
            <h2 className="mt-2 font-heading text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#f7efe4" }}>
              Selamat datang kembali
            </h2>
            <p className="mt-2 text-xs text-neutral-500">Masuk untuk melanjutkan perencanaan pernikahan Anda</p>
          </div>

          <div className="mt-6 space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Username / Email</Label>
              <Input
                data-testid="login-identifier"
                value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                placeholder="username atau email"
                className="mt-2 border-[#c9a35c33] bg-[#141010] text-white placeholder:text-neutral-600 focus-visible:ring-[#c9a35c]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Password</Label>
              <div className="relative mt-2">
                <Input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="password"
                  className="border-[#c9a35c33] bg-[#141010] pr-10 text-white placeholder:text-neutral-600 focus-visible:ring-[#c9a35c]"
                />
                <button type="button" data-testid="toggle-password" onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[#e6c98a]">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link to="/lupa-password" data-testid="forgot-password-link" className="text-xs text-neutral-400 underline decoration-[#c9a35c66] underline-offset-4 hover:text-[#e6c98a]">
                  Lupa password?
                </Link>
              </div>
            </div>
          </div>

          <Button
            data-testid="login-submit"
            type="submit" disabled={loading}
            className="group mt-8 h-12 w-full rounded-full font-semibold text-[#0a0705] transition-transform hover:-translate-y-0.5"
            style={{ background: "linear-gradient(90deg,#c9a35c,#e6c98a)" }}
          >
            {loading ? "Memproses..." : (<span className="flex items-center gap-2">Masuk <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>)}
          </Button>

          <p className="mt-6 text-center text-xs text-neutral-500">
            Sesi otomatis keluar setelah 15 menit tidak aktif.
          </p>
          <p className="mt-4 text-center text-sm text-neutral-400">
            Belum punya akun?{" "}
            <Link to="/daftar" data-testid="link-to-register" className="font-semibold text-[#e6c98a] underline decoration-[#c9a35c66] underline-offset-4">
              Daftar sekarang
            </Link>
          </p>
        </motion.form>
      </div>
    </div>
  );
}
